import React, { useState, useEffect } from "react";
import {
  X,
  Building2,
  Users,
  MapPin,
  Mail,
  Phone,
  Shield,
  ChevronRight,
  Edit,
  UserPlus,
  Network,
  Calendar,
  Layers,
  ExternalLink,
  Loader,
  Lock,
  CheckCircle,
  XCircle,
  Building,
  GitBranch,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSuspension } from "../../contexts/SuspensionContext";
import { fetchChildOrgDetailsAPI } from "../../actioncreators/actioncreators";

const ChildOrgDetails = ({ isOpen, onClose, orgId, onAssignAdmin }) => {
  const [orgDetails, setOrgDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const { isSuspended } = useSuspension();

  useEffect(() => {
    if (isOpen && orgId) {
      fetchDetails();
    }
  }, [isOpen, orgId]);

  const fetchDetails = async () => {
    setIsLoading(true);
    try {
      const response = await fetchChildOrgDetailsAPI(orgId);
      if (response?.businessStatusCode === 2) {
        setOrgDetails(response.data);
      } else {
        toast.error(response?.message || "Failed to fetch details");
      }
    } catch (error) {
      console.error("Error fetching org details:", error);
      toast.error("Failed to fetch organization details");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const isActive = status === "ACTIVE";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: isActive ? "#d1fae5" : "#fee2e2",
          color: isActive ? "#059669" : "#dc2626",
        }}
      >
        {isActive ? <CheckCircle size={14} /> : <XCircle size={14} />}
        {status}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.4)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 1050,
        animation: "fadeIn 0.2s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          backgroundColor: "#fff",
          height: "100%",
          overflow: "hidden",
          boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
          animation: "slideIn 0.3s ease",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading ? (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
            }}
          >
            <Loader
              size={32}
              color="#ff7a00"
              style={{ animation: "spin 1s linear infinite" }}
            />
            <p style={{ marginTop: "16px" }}>Loading organization details...</p>
          </div>
        ) : orgDetails ? (
          <>
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                color: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}
                >
                  <div
                    style={{
                      width: "56px",
                      height: "56px",
                      borderRadius: "14px",
                      backgroundColor: "rgba(255, 122, 0, 0.2)",
                      border: "2px solid #ff7a00",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Building2 size={28} color="#ff7a00" />
                  </div>
                  <div>
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        margin: 0,
                      }}
                    >
                      {orgDetails.org_name}
                    </h2>
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#9ca3af",
                        margin: "4px 0 0 0",
                      }}
                    >
                      {orgDetails.org_type || "Organization"}
                    </p>
                    <div style={{ marginTop: "8px" }}>
                      {getStatusBadge(orgDetails.status)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  style={{
                    padding: "8px",
                    border: "none",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              {/* Quick Stats */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "12px",
                  marginTop: "20px",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                  }}
                >
                  <Users size={18} style={{ marginBottom: "4px" }} />
                  <div style={{ fontSize: "18px", fontWeight: "700" }}>
                    {orgDetails.user_count || 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>Users</div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                  }}
                >
                  <MapPin size={18} style={{ marginBottom: "4px" }} />
                  <div style={{ fontSize: "18px", fontWeight: "700" }}>
                    {orgDetails.location_count || 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>Locations</div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                  }}
                >
                  <Layers size={18} style={{ marginBottom: "4px" }} />
                  <div style={{ fontSize: "18px", fontWeight: "700" }}>
                    {orgDetails.sub_org_count || 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>Sub-Orgs</div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "10px",
                  }}
                >
                  <Network size={18} style={{ marginBottom: "4px" }} />
                  <div style={{ fontSize: "18px", fontWeight: "700" }}>
                    {orgDetails.child_org_count || 0}
                  </div>
                  <div style={{ fontSize: "10px", color: "#9ca3af" }}>Children</div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#f9fafb",
              }}
            >
              {["overview", "hierarchy", "admin"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: "12px",
                    border: "none",
                    backgroundColor: "transparent",
                    borderBottom:
                      activeTab === tab ? "2px solid #ff7a00" : "2px solid transparent",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: activeTab === tab ? "#ff7a00" : "#6b7280",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 24px",
              }}
            >
              {activeTab === "overview" && (
                <>
                  {/* Contact Info */}
                  <div style={{ marginBottom: "24px" }}>
                    <h4
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "12px",
                      }}
                    >
                      Contact Information
                    </h4>
                    <div
                      style={{
                        backgroundColor: "#f9fafb",
                        borderRadius: "10px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Mail size={16} color="#6b7280" />
                        </div>
                        <div>
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#9ca3af",
                              margin: 0,
                            }}
                          >
                            Email
                          </p>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#1f2937",
                              margin: 0,
                            }}
                          >
                            {orgDetails.email || "Not provided"}
                          </p>
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Phone size={16} color="#6b7280" />
                        </div>
                        <div>
                          <p
                            style={{
                              fontSize: "11px",
                              color: "#9ca3af",
                              margin: 0,
                            }}
                          >
                            Mobile
                          </p>
                          <p
                            style={{
                              fontSize: "14px",
                              color: "#1f2937",
                              margin: 0,
                            }}
                          >
                            {orgDetails.mobile || "Not provided"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Created Date */}
                  <div style={{ marginBottom: "24px" }}>
                    <h4
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "12px",
                      }}
                    >
                      Timeline
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "16px",
                        backgroundColor: "#f9fafb",
                        borderRadius: "10px",
                      }}
                    >
                      <Calendar size={18} color="#6b7280" />
                      <div>
                        <p style={{ fontSize: "11px", color: "#9ca3af", margin: 0 }}>
                          Created On
                        </p>
                        <p style={{ fontSize: "14px", color: "#1f2937", margin: 0 }}>
                          {formatDate(orgDetails.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "hierarchy" && (
                <>
                  {/* Hierarchy Info */}
                  <div style={{ marginBottom: "24px" }}>
                    <h4
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "12px",
                      }}
                    >
                      Position in Hierarchy
                    </h4>

                    <div
                      style={{
                        backgroundColor: "#f9fafb",
                        borderRadius: "10px",
                        padding: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "16px",
                        }}
                      >
                        <GitBranch size={18} color="#ff7a00" />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                          }}
                        >
                          Level {orgDetails.hierarchy_level || 0}
                        </span>
                      </div>

                      {/* Path visualization */}
                      <div
                        style={{
                          padding: "12px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "11px",
                            color: "#9ca3af",
                            margin: "0 0 8px 0",
                          }}
                        >
                          Organization Path
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          {orgDetails.root_org_name && (
                            <>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  padding: "4px 8px",
                                  backgroundColor: "#f3f4f6",
                                  borderRadius: "4px",
                                }}
                              >
                                {orgDetails.root_org_name}
                              </span>
                              <ChevronRight size={14} color="#9ca3af" />
                            </>
                          )}
                          {orgDetails.parent_org_name &&
                            orgDetails.parent_org_name !== orgDetails.root_org_name && (
                              <>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: "#6b7280",
                                    padding: "4px 8px",
                                    backgroundColor: "#f3f4f6",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {orgDetails.parent_org_name}
                                </span>
                                <ChevronRight size={14} color="#9ca3af" />
                              </>
                            )}
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: "600",
                              color: "#ff7a00",
                              padding: "4px 8px",
                              backgroundColor: "#fff7ed",
                              borderRadius: "4px",
                            }}
                          >
                            {orgDetails.org_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Parent Org */}
                  {orgDetails.parent_org_name && (
                    <div style={{ marginBottom: "24px" }}>
                      <h4
                        style={{
                          fontSize: "12px",
                          fontWeight: "600",
                          color: "#6b7280",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "12px",
                        }}
                      >
                        Parent Organization
                      </h4>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "14px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "10px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Building size={20} color="#6b7280" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1f2937",
                              margin: 0,
                            }}
                          >
                            {orgDetails.parent_org_name}
                          </p>
                        </div>
                        <ExternalLink size={16} color="#9ca3af" />
                      </div>
                    </div>
                  )}

                  {/* Descendants Info */}
                  <div>
                    <h4
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "12px",
                      }}
                    >
                      Descendants
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          padding: "16px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "10px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "700",
                            color: "#1f2937",
                          }}
                        >
                          {orgDetails.child_org_count || 0}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          Direct Children
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "16px",
                          backgroundColor: "#f9fafb",
                          borderRadius: "10px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "700",
                            color: "#1f2937",
                          }}
                        >
                          {orgDetails.descendant_org_count || 0}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                          Total Descendants
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeTab === "admin" && (
                <>
                  {/* Admin Info */}
                  <div style={{ marginBottom: "24px" }}>
                    <h4
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "12px",
                      }}
                    >
                      Organization Administrator
                    </h4>

                    {orgDetails.admin_name ? (
                      <div
                        style={{
                          backgroundColor: "#f9fafb",
                          borderRadius: "10px",
                          padding: "16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                          }}
                        >
                          <div
                            style={{
                              width: "50px",
                              height: "50px",
                              borderRadius: "50%",
                              backgroundColor: "#ff7a00",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                              fontWeight: "700",
                              fontSize: "18px",
                            }}
                          >
                            {orgDetails.admin_name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "15px",
                                  fontWeight: "600",
                                  color: "#1f2937",
                                  margin: 0,
                                }}
                              >
                                {orgDetails.admin_name}
                              </p>
                              <span
                                style={{
                                  padding: "2px 8px",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  fontWeight: "600",
                                  backgroundColor: "#fff7ed",
                                  color: "#ff7a00",
                                }}
                              >
                                ADMIN
                              </span>
                            </div>
                            <p
                              style={{
                                fontSize: "13px",
                                color: "#6b7280",
                                margin: "4px 0 0 0",
                              }}
                            >
                              {orgDetails.admin_email}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          backgroundColor: "#fef3c7",
                          borderRadius: "10px",
                          padding: "20px",
                          textAlign: "center",
                          border: "1px dashed #fcd34d",
                        }}
                      >
                        <Shield size={32} color="#d97706" />
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#92400e",
                            margin: "12px 0 0 0",
                          }}
                        >
                          No administrator assigned
                        </p>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#b45309",
                            margin: "4px 0 0 0",
                          }}
                        >
                          Assign an admin to manage this organization
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={onAssignAdmin}
                    disabled={isSuspended}
                    style={{
                      width: "100%",
                      padding: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      border: "none",
                      borderRadius: "10px",
                      backgroundColor: isSuspended ? "#9ca3af" : "#ff7a00",
                      color: "#fff",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: isSuspended ? "not-allowed" : "pointer",
                      boxShadow: "0 2px 8px rgba(255, 122, 0, 0.3)",
                    }}
                  >
                    {isSuspended ? (
                      <Lock size={16} />
                    ) : (
                      <UserPlus size={16} />
                    )}
                    {orgDetails.admin_name ? "Change Admin" : "Assign Admin"}
                  </button>
                </>
              )}
            </div>
          </>
        ) : (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
            }}
          >
            <Building2 size={48} color="#d1d5db" />
            <p style={{ marginTop: "16px" }}>Organization not found</p>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ChildOrgDetails;
